// src/LandingPage.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  IoArrowBack, 
  IoLocationOutline, 
  IoFlashOutline, 
  IoPeopleOutline,
  IoChatbubbleEllipsesOutline
} from "react-icons/io5";
import { useLanguage } from "./contexts/LanguageContext";
import LanguageSelector from "./components/LanguageSelector";
import "./LandingPage.css";

function LandingPage() {
  const [counties, setCounties] = useState([]);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [stations, setStations] = useState([]);
  const [stationsWithAvailability, setStationsWithAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stationsError, setStationsError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  
  
  // Fetch counties when component mounts
  useEffect(() => {
    const fetchCounties = async () => {
      try {
        setLoading(true);
        console.log('Fetching counties...');
        const response = await fetch('/api/counties');
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Counties data:', data);
        
        // Fetch station counts for clickable counties
        const countiesWithCounts = await Promise.all(
          data.map(async (county) => {
            if (!county.clickable) {
              return { ...county, stationCount: 0 };
            }
            
            try {
              const stationsResponse = await fetch(`/api/counties/${county.name}/stations`);
              if (stationsResponse.ok) {
                const stations = await stationsResponse.json();
                return { ...county, stationCount: stations.length };
              }
            } catch (error) {
              console.error(`Error fetching station count for ${county.name}:`, error);
            }
            
            return { ...county, stationCount: 0 };
          })
        );
        
        setCounties(countiesWithCounts);
        
        // Check if there's a selected county from navigation state (when returning from station detail)
        if (location.state?.selectedCounty) {
          const countyToRestore = countiesWithCounts.find(
            county => county.id === location.state.selectedCounty.id
          );
          if (countyToRestore && countyToRestore.clickable) {
            setSelectedCounty(countyToRestore);
          }
        }
        
        setLoading(false);
        setError(null);
      } catch (error) {
        console.error("Error fetching counties:", error);
        setError(t('errorLoadingCounties'));
        setLoading(false);
      }
    };

    fetchCounties();
  }, []);

  // Fetch stations when a county is selected
  useEffect(() => {
    if (selectedCounty) {
      const fetchStations = async () => {
        try {
          setStationsLoading(true);
          setStationsError(null);
          
          console.log(`Fetching stations for ${selectedCounty.name} county...`);
          const response = await fetch(`/api/counties/${selectedCounty.name}/stations`);
          console.log('Response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('Stations data:', data);
          
          if (!Array.isArray(data)) {
            console.error('Expected array but got:', typeof data);
            throw new Error('Invalid data format received from server');
          }
          
          setStations(data);
          
          // Fetch availability data for each station
          const stationsWithAvailabilityData = await Promise.all(
            data.map(async (station) => {
              try {
                const availabilityResponse = await fetch(`/api/stations/${station.id}/availability`);
                if (availabilityResponse.ok) {
                  const availabilityData = await availabilityResponse.json();
                  return {
                    ...station,
                    available: availabilityData.availablePorts || 0,
                    availablePorts: availabilityData.availablePorts || 0,
                    totalPorts: availabilityData.totalPorts || 0
                  };
                }
              } catch (error) {
                console.error(`Error fetching availability for station ${station.id}:`, error);
              }
              
              return {
                ...station,
                available: 0,
                availablePorts: 0,
                totalPorts: 0
              };
            })
          );
          
          setStationsWithAvailability(stationsWithAvailabilityData);
          setStationsLoading(false);
        } catch (error) {
          console.error("Error fetching stations:", error);
          setStationsError(t('errorLoadingStations'));
          setStationsLoading(false);
          setStations([]);
        }
      };

      fetchStations();
    } else {
      setStations([]);
      setStationsError(null);
    }
  }, [selectedCounty]);
  
  const handleCountyClick = (county) => {
    if (county.clickable) {
      console.log('Selected county:', county);
      setSelectedCounty(county);
    }
  };

  const handleClearSelection = () => {
    setSelectedCounty(null);
    setStations([]);
    setStationsError(null);
  };
  
  const handleStationClick = (station) => {
    navigate(`/station/${station.id}`, { 
      state: { 
        station,
        selectedCounty // Pass the selected county to preserve it
      } 
    });
  };

  const handleFeedbackClick = () => {
    navigate('/feedback');
  };

  if (loading) {
    return (
      <div className="landing-container">
        <div className="loading">
          <IoFlashOutline size={32} />
          <p>{t('loadingCounties')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-container">
        <div className="error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-container">
      <LanguageSelector />
      <div className="header">
        <div className="header-content">
          <div className="header-text">
            <h1>{t('title')}</h1>
            <p>{t('subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="content">
        {/* Desktop Layout - Sidebar */}
        <div className="main-layout desktop-layout">
          {/* County Selection */}
          <div className="county-selection">
            <h2>Select County</h2>
            <div className="county-grid">
              {counties.map((county) => (
                <div
                  key={county.id}
                  className={`county-card ${county.clickable ? "clickable" : "disabled"} ${
                    selectedCounty && selectedCounty.id === county.id ? "selected" : ""
                  }`}
                  onClick={() => handleCountyClick(county)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
                    <IoLocationOutline size={20} color={county.clickable ? "var(--color-primary)" : "var(--color-gray-400)"} />
                  </div>
                  <h3>{county.name}</h3>
                  {!county.clickable ? (
                    <div className="coming-soon">{t('comingSoon')}</div>
                  ) : (
                    <div className="station-count">{county.stationCount} {county.stationCount === 1 ? t('station') : t('stations')}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Station Content Area */}
          <div className="station-content">
            {!selectedCounty ? (
              <div className="empty">
                <div style={{ textAlign: 'center', color: 'var(--color-gray-500)' }}>
                  <IoLocationOutline size={48} color="var(--color-gray-400)" />
                  <h3>{t('selectCounty')}</h3>
                  <p>{t('selectCountyPrompt')}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="station-header">
                  <h2>Electrify America {t('stationsIn')} {selectedCounty.name} {t('county')}</h2>
                                      <button className="back-button" onClick={handleClearSelection}>
                    <IoArrowBack size={16} />
                    {t('back')}
                  </button>
                </div>
                
                {stationsLoading ? (
                  <div className="loading">
                    <IoFlashOutline size={24} />
                    <p>{t('loadingStations')}</p>
                  </div>
                ) : stationsError ? (
                  <div className="error">{stationsError}</div>
                ) : stationsWithAvailability.length === 0 ? (
                  <div className="no-stations">
                    <IoLocationOutline size={32} color="var(--color-gray-400)" />
                    <p>{t('noStationsFound')}</p>
                  </div>
                ) : (
                  <div className="station-grid">
                    {stationsWithAvailability.map((station) => (
                      <div
                        key={station.id}
                        className={`station-card clickable ${station.availablePorts > 0 ? 'has-available' : ''}`}
                        onClick={() => handleStationClick(station)}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                          <IoFlashOutline size={20} color="var(--color-success)" />
                          <div style={{ flex: 1 }}>
                            <h3>{station.name}</h3>
                            <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0 }}>
                              <IoLocationOutline size={14} color="var(--color-gray-500)" />
                              {station.address}
                            </p>
                          </div>
                        </div>
                        <div className="station-details">
                          <span className={`availability-badge ${station.availablePorts > 0 ? 'available' : 'unavailable'}`}>
                            {t('portsAvailable', { available: station.availablePorts, total: station.totalPorts })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Mobile Layout - Inline Expansion */}
        <div className="mobile-layout">
          <div className="county-selection">
            <h2>{t('selectCounty')}</h2>
            <div className="county-grid">
              {counties.map((county) => (
                <React.Fragment key={county.id}>
                  <div
                    className={`county-card ${county.clickable ? "clickable" : "disabled"} ${
                      selectedCounty && selectedCounty.id === county.id ? "selected" : ""
                    }`}
                    onClick={() => handleCountyClick(county)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
                      <IoLocationOutline size={20} color={county.clickable ? "var(--color-primary)" : "var(--color-gray-400)"} />
                    </div>
                    <h3>{county.name}</h3>
                    {!county.clickable ? (
                                              <div className="coming-soon">{t('comingSoon')}</div>
                    ) : (
                      <div className="station-count">{county.stationCount} {county.stationCount === 1 ? t('station') : t('stations')}</div>
                    )}
                  </div>
                  
                  {/* Show stations right below selected county on mobile */}
                  {selectedCounty && selectedCounty.id === county.id && (
                    <div className="inline-stations">
                      <div className="station-header">
                        <h3>Electrify America {t('stationsIn')} {selectedCounty.name} {t('county')}</h3>
                        <button className="clear-button" onClick={handleClearSelection}>
                          <IoArrowBack size={16} />
                          {t('back')}
                        </button>
                      </div>
                      
                      {stationsLoading ? (
                        <div className="loading">
                          <IoFlashOutline size={24} />
                          <p>{t('loadingStations')}</p>
                        </div>
                      ) : stationsError ? (
                        <div className="error">{stationsError}</div>
                      ) : stationsWithAvailability.length === 0 ? (
                                                  <div className="no-stations">
                            <IoLocationOutline size={32} color="var(--color-gray-400)" />
                            <p>{t('noStationsFound')}</p>
                          </div>
                      ) : (
                        <div className="station-grid">
                          {stationsWithAvailability.map((station) => (
                            <div
                              key={station.id}
                              className={`station-card clickable ${station.availablePorts > 0 ? 'has-available' : ''}`}
                              onClick={() => handleStationClick(station)}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                                <IoFlashOutline size={20} color="var(--color-success)" />
                                <div style={{ flex: 1 }}>
                                  <h4>{station.name}</h4>
                                  <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0 }}>
                                    <IoLocationOutline size={14} color="var(--color-gray-500)" />
                                    {station.address}
                                  </p>
                                </div>
                              </div>
                              <div className="station-details">
                                <span className={`availability-badge ${station.availablePorts > 0 ? 'available' : 'unavailable'}`}>
                                  {t('portsAvailable', { available: station.availablePorts, total: station.totalPorts })}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        
        {/* Feedback Button - At bottom of page */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8) var(--space-4) var(--space-6)' }}>
          <button className="feedback-button" onClick={handleFeedbackClick}>
            <IoChatbubbleEllipsesOutline size={20} />
            {t('feedbackButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LandingPage;